import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { AssetsMapService } from '@pages/assets-page/services/assets-map.service';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { ColDef, IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  interval,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AssetsGridService } from './assets-grid.service';
import { RestApiService } from '@core/services/rest-api.service';
import { AssetDto } from '@pages/assets-page/components/interfaces';
import { AutoZoomFitParams, MapInput } from '@features/map/interfaces';
import { environment } from '@env/environment';
import { AssetViewType } from '@pages/assets-page/components/constants';
import { HardwareDeviceStatus } from '@shared/constants/hardware-device-status';
import { MapSetting } from '@features/map/constants/constants';
import {
  DvSplitterEventsService,
  DvSplitterStatus,
} from '@devinco/ui/dds/splitter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-assets-grid',
  templateUrl: './assets-grid.component.html',
  styleUrls: ['./assets-grid.component.scss'],
  providers: [GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssetsGridComponent implements OnInit {
  private gridOptionsService = inject(GridOptionsService);
  private assetGridService = inject(AssetsGridService);
  private mapService = inject(AssetsMapService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private api = inject(RestApiService);
  private splitterEvents = inject(DvSplitterEventsService);

  @Input({ required: true }) set activeView(value: AssetViewType) {
    this.isGridLoading.set(true);
    this.currentActiveView = value;
    this.setGridOptionsForView(value);
  }
  @Output() splitterStatusChange = new EventEmitter<string>();

  actions = signal<DvToolbarAction[]>([]);
  gridOptions: GridOptions<AssetDto>;
  isGridLoading = signal<boolean>(true);
  readonly mapSetting = MapSetting.AssetsGridMap;

  @ViewChild('menuPanelTemplate')
  menuPanelContent: TemplateRef<TimedMarkerMapMenuPanelComponent>;

  private currentActiveView: AssetViewType;
  private selectedNode?: IRowNode;
  private canDelete: boolean;
  private selectedAssetMarker$ = new BehaviorSubject<MapInput[]>([]);
  private refreshSubscription: Subscription | undefined;
  private autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: false,
  });

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.actions.set(this.assetGridService.getGridActions(this.showAssetOnMap));
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.selectedNode = api.getSelectedNodes()[0] as
      | IRowNode<AssetDto>
      | undefined;

    if (
      this.selectedNode &&
      this.currentActiveView === AssetViewType.TreeView &&
      !this.selectedNode.data?.IsAsset
    ) {
      this.changeVerticalSplitter(false);
      this.actions.update(actions =>
        actions.map(action => {
          if (
            action.icon === 'delete' ||
            action.icon === 'edit' ||
            action.icon === 'map_marker_move'
          ) {
            return { ...action, disabled: true };
          }
          return action;
        })
      );
      return;
    } else if (this.currentActiveView === AssetViewType.TreeView) {
      this.changeVerticalSplitter(true);
    }

    if (!this.selectedNode) {
      this.changeVerticalSplitter(true);
    }

    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.AssetDelete]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.AssetShowInMap,
      ]),
    ])
      .pipe(take(1))
      .subscribe(([canDelete, canSeeMap]) => {
        this.canDelete = canDelete;

        this.actions.update(actions =>
          actions.map(action => {
            if (action.icon === 'delete') {
              return {
                ...action,
                disabled: !this.canDelete || !this.selectedNode,
              };
            }
            if (action.icon === 'edit') {
              return { ...action, disabled: !this.selectedNode };
            }
            if (action.icon === 'map_marker_move') {
              return {
                ...action,
                disabled:
                  !canSeeMap ||
                  !this.selectedNode ||
                  !this.selectedNode.data?.FindActive ||
                  this.selectedNode.data?.FindActive ===
                    HardwareDeviceStatus.Inactive,
              };
            }
            return action;
          })
        );
      });
  }

  onAutoRefreshClicked(isAutoRefreshOn: boolean): void {
    if (isAutoRefreshOn) {
      this.refreshSubscription = interval(environment.mapRefreshInterval)
        .pipe(
          switchMap(() =>
            this.getSingleVehicleLatestData(this.selectedNode!.data).pipe(
              tap(resp => {
                this.selectedAssetMarker$.next(
                  this.mapService.getMapMarkers([resp])
                );
              })
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.refreshSubscription?.unsubscribe();
    }
  }

  onAutoZoomFitClicked(isAutoZoomOn: boolean): void {
    this.autoZoomFit$.next({ doAutoFit: isAutoZoomOn });
  }

  private setGridOptionsForView(viewType: AssetViewType): void {
    switch (viewType) {
      case AssetViewType.RegularView: {
        this.gridOptions = this.gridOptionsService.clientSide(
          this.assetGridService.getColDefs(),
          Entity.Asset
        );
        break;
      }
      case AssetViewType.TreeView: {
        this.gridOptions = {
          ...this.gridOptionsService.clientSideTreeData<AssetDto>(
            [
              ...this.assetGridService.getColDefs(),
              ...this.assetGridService.getAssetByTypeExtraColDefs(),
            ],
            Entity.AssetByType,
            {
              groupDescriptionField: 'Description',
              idField: 'AssetID',
              parentField: 'ParentAssetID',
            }
          ),
        };
        break;
      }
    }

    // makes sure change detection sees grid reappearing
    setTimeout(() => {
      this.isGridLoading.set(false);
    });
  }

  private showAssetOnMap = (): void => {
    if (!this.selectedNode?.data) return;

    this.api
      .getInfo<AssetDto>(
        Entity.Asset,
        this.selectedNode.data.AssetID,
        this.assetGridService
          .getColDefs()
          .map((colDef: ColDef) => colDef.field!)
          .filter(field => field)
      )
      .pipe(
        switchMap(asset => {
          this.selectedAssetMarker$.next(
            this.mapService.getMapMarkers([asset])
          );
          return this.mapService.showAssetsOnMap(
            this.selectedAssetMarker$,
            this.menuPanelContent,
            this.autoZoomFit$
          ).closed;
        })
      )
      .pipe(take(1))
      .subscribe(() => this.refreshSubscription?.unsubscribe());
  };

  private getSingleVehicleLatestData(
    selectedNodeData: AssetDto
  ): Observable<AssetDto> {
    return this.api.getInfo<AssetDto>(
      Entity.Asset,
      String(selectedNodeData.AssetID),
      this.assetGridService
        .getColDefs()
        .map((colDef: ColDef) => colDef.field!)
        .filter(field => field)
    );
  }

  private changeVerticalSplitter(isOpened: boolean): void {
    if (isOpened) {
      this.splitterStatusChange.emit(DvSplitterStatus.Open);
      this.splitterEvents.isSplitterOpened$.next(true);
    } else {
      this.splitterStatusChange.emit(DvSplitterStatus.Closed);
      this.splitterEvents.isSplitterOpened$.next(false);
    }
  }
}
